import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * GDPR Admin Authentication Context
 *
 * Provides a separate authentication path specifically for GDPR Admin access.
 * Features:
 * - Only allows the specific username 'gdpradmin2026'
 * - 15-minute session timeout with activity-based extension
 * - Credentials verified against backend (which reads from Azure Key Vault)
 * - Session stored in sessionStorage (cleared on browser close)
 */

const GdprAdminAuthContext = createContext(null);

const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes in milliseconds
const STORAGE_KEY = 'gdpr_admin_session';

export const GdprAdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(null);
  const [error, setError] = useState(null);
  const [timeoutWarning, setTimeoutWarning] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = () => {
      const session = sessionStorage.getItem(STORAGE_KEY);
      if (session) {
        try {
          const { timestamp, authenticated } = JSON.parse(session);
          const now = Date.now();
          const elapsed = now - timestamp;

          if (authenticated && elapsed < SESSION_TIMEOUT_MS) {
            setIsAuthenticated(true);
            setLastActivity(timestamp);
          } else {
            // Session expired
            sessionStorage.removeItem(STORAGE_KEY);
            setIsAuthenticated(false);
          }
        } catch (e) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };

    checkSession();
  }, []);

  // Session timeout checker
  useEffect(() => {
    if (!isAuthenticated || !lastActivity) return;

    const checkTimeout = () => {
      const now = Date.now();
      const elapsed = now - lastActivity;
      const remaining = SESSION_TIMEOUT_MS - elapsed;

      // Show warning 2 minutes before timeout
      if (remaining <= 2 * 60 * 1000 && remaining > 0) {
        setTimeoutWarning(true);
      } else {
        setTimeoutWarning(false);
      }

      // Session expired
      if (elapsed >= SESSION_TIMEOUT_MS) {
        logout('Session expired due to inactivity');
      }
    };

    // Check every 30 seconds
    const interval = setInterval(checkTimeout, 30000);
    checkTimeout(); // Initial check

    return () => clearInterval(interval);
  }, [isAuthenticated, lastActivity]);

  // Update activity timestamp
  const updateActivity = useCallback(() => {
    if (isAuthenticated) {
      const now = Date.now();
      setLastActivity(now);
      setTimeoutWarning(false);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        authenticated: true,
        timestamp: now
      }));
    }
  }, [isAuthenticated]);

  // Track user activity (mouse, keyboard, scroll)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleActivity = () => {
      updateActivity();
    };

    // Throttle activity updates to once per minute
    let lastUpdate = 0;
    const throttledActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > 60000) {
        lastUpdate = now;
        handleActivity();
      }
    };

    window.addEventListener('mousemove', throttledActivity);
    window.addEventListener('keypress', throttledActivity);
    window.addEventListener('scroll', throttledActivity);
    window.addEventListener('click', throttledActivity);

    return () => {
      window.removeEventListener('mousemove', throttledActivity);
      window.removeEventListener('keypress', throttledActivity);
      window.removeEventListener('scroll', throttledActivity);
      window.removeEventListener('click', throttledActivity);
    };
  }, [isAuthenticated, updateActivity]);

  /**
   * Login function - validates credentials against backend
   * Only accepts the specific username 'gdpradmin2026'
   */
  const login = async (username, password) => {
    setError(null);
    setIsLoading(true);

    // Client-side validation: only allow the specific username
    if (username !== 'gdpradmin2026') {
      setError('Invalid credentials');
      setIsLoading(false);
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/gdpradm/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.authenticated) {
        const now = Date.now();
        setIsAuthenticated(true);
        setLastActivity(now);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          authenticated: true,
          timestamp: now
        }));
        setIsLoading(false);
        return true;
      } else {
        setError(data.detail || 'Invalid credentials');
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      console.error('GDPR Admin login error:', err);
      setError('Unable to connect to authentication service');
      setIsLoading(false);
      return false;
    }
  };

  /**
   * Logout function - clears session
   */
  const logout = useCallback((message = null) => {
    setIsAuthenticated(false);
    setLastActivity(null);
    setTimeoutWarning(false);
    sessionStorage.removeItem(STORAGE_KEY);
    if (message) {
      setError(message);
    }
  }, []);

  /**
   * Get remaining session time in seconds
   */
  const getRemainingTime = useCallback(() => {
    if (!lastActivity) return 0;
    const elapsed = Date.now() - lastActivity;
    const remaining = Math.max(0, SESSION_TIMEOUT_MS - elapsed);
    return Math.floor(remaining / 1000);
  }, [lastActivity]);

  /**
   * Extend session by updating activity
   */
  const extendSession = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  const value = {
    isAuthenticated,
    isLoading,
    error,
    timeoutWarning,
    login,
    logout,
    getRemainingTime,
    extendSession,
    clearError: () => setError(null),
  };

  return (
    <GdprAdminAuthContext.Provider value={value}>
      {children}
    </GdprAdminAuthContext.Provider>
  );
};

export const useGdprAdminAuth = () => {
  const context = useContext(GdprAdminAuthContext);
  if (!context) {
    throw new Error('useGdprAdminAuth must be used within a GdprAdminAuthProvider');
  }
  return context;
};

export default GdprAdminAuthContext;
